import { push } from 'immutadot/array';
import { set, unset } from 'immutadot/core';

import { get } from 'immutadot';
import {deepCopy} from "../utils";
import { walkTree, isSpecialValue } from "../utils/tree-walker";
export function isObject(obj) {
    return obj === Object(obj);
}

// converts [{id:1,name:'fred'},{id:21,name:'bert'}] into {1:'fred',21:'bert} for simplicity
function convertArrayToObject(array: unknown): Record<string, unknown> {
    if (array == null) { array = []; }
    if (!Array.isArray(array)) return array as Record<string, unknown>;
    const object: Record<string, unknown> = {};
    (array as { id: unknown }[]).forEach((v) => {
        object[String(v.id)] = v;
    });
    return object;
}


// work out a path to an object, suitable for immutable's SET() command


function getAbsolutePath(object:any, path:string|string[], allowNew = false) {
    // allow a single creation, not a double
    const parts = Array.isArray(path) ? path : path.split('.');
    let currentValue = object;
    const newPath:string[] = [];
    let noPath = false;
    let noArrayItem = false;
    parts.forEach((pathPart) => {
        if (currentValue == null) {
            noPath = true;
            newPath.push('.' + pathPart);
            return;
        }
        if (Array.isArray(currentValue)) {
            let newReturnValue = null;
            let idx:number|null = null;
            currentValue.forEach((v, k) => {
                if (v.id == pathPart) {
                    newReturnValue = v;
                    idx = k;
                }
            });
            if (newReturnValue) {
                newPath.push('[' + idx + ']');
            } else {
                noArrayItem = true;
                noPath = true;
            }
            currentValue = newReturnValue;
            return;
        }
        if (isObject(currentValue)) {
            if (currentValue[pathPart]) {
                newPath.push('.' + pathPart);
                currentValue = currentValue[pathPart];
            } else {
                if (allowNew) {
                    newPath.push('.' + pathPart);
                    currentValue = null;
                }
            }
            return;
        }
        noPath = true;
        newPath.push('.' + pathPart);
        currentValue = currentValue[pathPart] ?? null;
        return;
    });
    if (noPath && !allowNew) return null;
    if (noArrayItem && allowNew) return null;
    const rv = newPath.join('');
    if (rv[0] == '.') { return rv.substring(1); }
    return rv;
}

type IterateObjectsCallbackProps = {
    key: string[],
    objects: unknown[],
    isArray: boolean,
    isObject?: boolean,
    // one entry per key segment; true where the segment is an array element id
    arrayMask: boolean[]
}

type IterateObjectsCallback = (props: IterateObjectsCallbackProps) => boolean;

// iterate through the object tree, preserving path and calling toCall on all
// items. Reimplemented over the shared walker: the walker yields every node in
// pre-order; a container root is not visited (matching the legacy behaviour of
// calling the callback for a container's keys, not the container itself); a
// callback returning false prunes the node's subtree (skipped by depth).
export const iterateObjects = (object1: unknown, object2: unknown, toCall: IterateObjectsCallback): void => {
    let pruneDepth = -1;
    for (const node of walkTree(object1, object2, {})) {
        if (node.depth === 0 && (node.shape === 'object' || node.shape === 'array')) continue;
        if (pruneDepth !== -1) {
            if (node.depth > pruneDepth) continue;
            pruneDepth = -1;
        }
        const descend = toCall({
            key: node.name,
            objects: node.values,
            // the callback's isArray is the parent's isArray: the last element of
            // this node's arrayMask (the node's own shape is not what's wanted)
            isArray: node.arrayMask.length ? node.arrayMask[node.arrayMask.length - 1] : false,
            arrayMask: node.arrayMask,
        });
        if (!descend) pruneDepth = node.depth;
    }
};

// legacy (pre-walker) iteration, retained as the reference implementation for
// differential tests (old engine vs new engine). Behaviour is identical to the
// walker-based `iterateObjects` above.
export const iterateObjectsLegacy = (object1: unknown, object2: unknown, toCall: IterateObjectsCallback, prefix: string[] = [], arrayMask: boolean[] = []): void => {
    if (['string', 'number', 'boolean', 'bigint'].indexOf(typeof object1) > -1 || ['string', 'number', 'boolean', 'bigint'].indexOf(typeof object2) > -1) {
        toCall({ key: prefix, objects: [object1, object2], isArray: false, isObject: false, arrayMask });
        return;
    }
    if (isSpecialValue(object1) || isSpecialValue(object2)) {
        toCall({ key: prefix, objects: [object1, object2], isArray: false, arrayMask });
        return;
    }
    const forced1: Record<string, unknown> = (object1 == null ? {} : object1) as Record<string, unknown>;
    const forced2: Record<string, unknown> = (object2 == null ? {} : object2) as Record<string, unknown>;
    const isArray = Array.isArray(object1) || Array.isArray(object2);
    const obj1 = isArray ? convertArrayToObject(object1) : forced1;
    const obj2 = isArray ? convertArrayToObject(object2) : forced2;
    let keys = Object.keys(obj1).concat(Object.keys(obj2));
    keys = keys.filter((item, pos) => keys.indexOf(item) === pos);
    keys.forEach((key: string) => {
        const k1 = obj1[key] ?? null, k2 = obj2[key] ?? null;
        const childMask = arrayMask.concat(isArray);
        const descend = toCall({ key: prefix.concat(key), objects: [k1, k2], isArray: isArray, arrayMask: childMask });
        if (!descend) return;
        iterateObjectsLegacy(k1, k2, toCall, prefix.concat([key]), childMask);
    });
};

// not immutable safe!!
// finds everything with a creation_id, records it and then deletes the creation_id
export const extractCreationIds = (object1: unknown): unknown => {
    const creationIds = [];
    iterateObjects(object1, {}, ({ key: names, objects: objects, isArray: isArray }) => {
        console.log('kiterate',names,objects,isArray);
        if (Array.isArray(objects[0])) {
            return true;
        }
        const o1: any = objects[0];
        if (o1 == null) return false;
        if (typeof o1 != 'object') {
            return false;
        }
        if(o1.creation_id) {
            // the new value is always the ID
            //const origName = (isArray ? o1.id : names.pop());
            // if it's an array we use the ID for the index, otherwise use the attribute name
            let idPath = [],newIdPath = [];
            if(isArray) {
                idPath = [...names];
                idPath.pop();
                    idPath =  idPath.concat(o1.creation_id);
                newIdPath = [...names];
                newIdPath.pop();
                newIdPath = newIdPath.concat(o1.id);
//                    idPath.join('.');
//                idPath.pop();
//                idPath.push(o1.id);
            } else {
                idPath = names;
                newIdPath = names;
            }
            creationIds.push({ newId: o1.id, newIdPath: newIdPath.join('.'), oldIdPath: idPath.join('.') });
        }
        return true;
    });
    return creationIds;
}


// remove all the creationIds from the object tree
export const purgeCreationIds = (object1, creationIds: any): unknown => {
    creationIds.forEach((paths) => {
        const directPath = getAbsolutePath(object1, paths.newIdPath);
        console.warn('paths are',paths,directPath,object1);
        object1 = unset(object1, directPath+ '.creation_id');
    });
    return object1;
};

// rename any record that needs to be renamed according to creation_ids
export const applyCreationIds = (object1, creationIds: any): unknown => {
    creationIds.forEach((paths) => {
        const path = getAbsolutePath(object1, paths.oldIdPath);
        if (!path) return;
        object1 = set(object1, path + '.id', paths.newId);
    });
    return object1;
};

type ObjectDiff = {
    type:string,
    name:string[],
    isArray:boolean,
    // one entry per name segment; true where the segment is an array element id
    arrayMask: boolean[],
    value?:string|object|number|null|unknown
};


// generate a quick diff of 2 objects
export const diffObjects = (object1: unknown, object2: unknown, iterateFn: typeof iterateObjects = iterateObjects): ObjectDiff[] => {
    const results:ObjectDiff[] = [];
    iterateFn(object1, object2, ({ key: name, objects: [o1, o2], isArray: isArray, arrayMask }) => {
        if (o1 && o2 == null) {
            results.push({
                type:'D', 
                name:name,
                isArray:isArray,
                arrayMask
            });
            return false;
        }
        if (o2 && o1 == null) {
            if (typeof (o2) == 'object') {
                console.log('diffs are cloned',o2);
                if (Array.isArray(o2)) {
                    const val = o2.map((v) => deepCopy(v));
                    results.push({
                        type:'A',name:name,isArray:isArray,value:val, arrayMask});
                } else {
                    const val = deepCopy(o2);
                    console.log('value is',val);
                    results.push({type:'A', name, isArray, value:val, arrayMask});
                }
            } else {
                results.push({type:'A', name, isArray, value: o2, arrayMask});
            }
            return false;
        }
        if (['string', 'number', 'boolean', 'bigint'].indexOf(typeof o1) > -1 || ['string', 'number', 'boolean', 'bigint'].indexOf(typeof o2) > -1) {
            if (o1 != o2) {
                results.push({type:'E', name, isArray, value:o2, arrayMask});
            }
            return false;
        }
        if (typeof (o2) == 'object' && typeof (o1) == 'object' && o1 && o2 && (o1 as any).id != (o2 as any).id) {
            const val = deepCopy(o2);
            results.push({type:'E', name, isArray, value:val, arrayMask});
            return false;
        }
        return true;
    });
    return results;
}


// walk `name` over `target`; at each segment flagged by `arrayMask`, the current node
// must be an array containing an element with that id. Unflagged segments may be
// missing (allowNew creates them). Returns the node at the end of the walk, or null
// when a flagged segment does not resolve.
function walkPath(target: unknown, name: string[], arrayMask: boolean[]): unknown {
    let node: unknown = target;
    for (let i = 0; i < name.length; i++) {
        if (arrayMask[i]) {
            if (!Array.isArray(node)) return null;
            const element = (node as { id?: unknown }[]).find((v) => v && v.id == name[i]);
            if (!element) return null;
            node = element;
        } else if (node != null) {
            node = (node as Record<string, unknown>)[name[i]];
        }
    }
    return node;
}
export const applyDiffs = (targetObject: any, diffs: ObjectDiff[]): unknown => {
    console.log('id-diff:APPLY', diffs, targetObject);
    // try to apply every change in diffs to targetObject
    diffs.forEach((diff) => {
        // if the source had an array element at this position, the target must still
        // have that array and element; otherwise the server removed the collection -
        // drop the local edit instead of creating an ID-keyed object where an array belongs
        if (diff.arrayMask) {
            if (diff.isArray && diff.type == 'A') {
                // new array element: only the parent path needs to resolve (the element itself is new)
                const parent = walkPath(targetObject, diff.name.slice(0, -1), diff.arrayMask);
                if (parent == null || !Array.isArray(parent)) return;
            } else if (walkPath(targetObject, diff.name, diff.arrayMask) === null) {
                return;
            }
        }
        if (diff.isArray && diff.type == 'A') {
            // if the child node already exists, ignore the change
            let targetPath = getAbsolutePath(targetObject, diff.name);
            console.log('id-diff apply to Array', {target:targetObject, path:targetPath, source:diff.value});
            // this may or may not be correct
            if (!targetPath) {

                // for an array insertion, we only want the parent node
                diff.name.pop();
                targetPath = getAbsolutePath(targetObject, diff.name);
                console.log('id-diff:targetPath', targetPath, diff.name);
                if (!targetPath) return;
                console.log('id-diff:PUSH', targetObject, targetPath, diff.value);
                targetObject = push(targetObject, targetPath, diff.value);
                return;
            }
        }
        const targetPath = getAbsolutePath(targetObject, diff.name, diff.type == 'A'||diff.type=='E'||diff.type=='D');
        if (!targetPath) return;
        if (diff.isArray) {
            console.log('id-diff:DELETE', get(targetObject, targetPath));
            switch (diff.type) {
                case 'D':
                    targetObject = unset(targetObject, targetPath);
                    break;
                /*                case 'A':
                                case 'E':
                                    console.log('diffs SET',{object:targetObject,diff:diff,path:targetPath,value:diff[3]});
                                    targetObject = set(targetObject, targetPath, diff[3]);
                                break;*/
            }
            console.log('id-diff:POST-DELETE', get(targetObject, targetPath));
            return;
        }
        switch (diff.type) {
            case 'A':
            case 'E':
                console.log('id-diff:SET', { object: targetObject.a, diff: diff, path: targetPath, value: diff.value });
                targetObject = set(targetObject, targetPath, diff.value);
                break;
            case 'D':
                console.log('id-diff:DELETEB', { object: targetObject.a, diff: diff, path: targetPath, value: diff.value });
                targetObject = unset(targetObject, targetPath);
                break;
        }
    });
    console.log('id-diff:Applied, returning', targetObject);
    return targetObject;
}